import { useState, useEffect, useRef } from "react";
import styled from "styled-components";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";

import {
  Stack,
  IconButton,
  Typography,
  TextField,
  Autocomplete,
  Box,
  Chip,
} from "@mui/material";
import { DialogTitle, DialogContent, DialogActions } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import BlogCard from "src/components/blog/BlogCard";

import { createFilterOptions } from "@mui/material";

import { useDialog } from "src/context/DialogContext";
import { useSnackbar } from "notistack";

import { arrayMove } from "@dnd-kit/sortable";
import DraggableBoard, {
  DraggableItem,
} from "src/components/core/DraggableBoard";

import font from "src/styles/font";
import { hexToRgba } from "src/common/color";
import { Black, DarkGrey, White } from "src/styles/color";

import { MAX_BLOG_SUGGESTION } from "./BlogSuggestion";

import moment from "moment";

import { getBlogSuggest, updateBlogSuggest } from "src/api/landingPage";
import { getAllBlog } from "src/api/blog";

const S = {};

const ImgDisplay = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  object-position: center;
  border-radius: 8px;
  flex-shrink: 0;
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f2f1f3;
  border-radius: 16px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  margin-right: 8px;
  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

S.BlogSuggestionContainer = styled.div`
  width: fit-content;
  display: grid;
  grid-template-columns: repeat(5, minmax(0px, 1fr));
  gap: 16px;
  margin: 0 auto;
`;

S.BlogCardDraggable = styled(DraggableItem)`
  position: relative;
`;

S.BlogCard = styled(BlogCard)`
  width: 100%;
  padding: 8px;
  cursor: pointer;
`;

S.BlogOptionContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(10, 1fr);
`;

S.HoverContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  opacity: 0;
  transition: all 0.2s;

  &:hover {
    opacity: 1;
  }
`;

S.ItemBackdrop = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${hexToRgba(Black, 0.5)};
  border-radius: 16px;
`;

S.ItemActionMenu = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 8px;
`;

const BlogSuggestionAddDialog = ({ onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog } = useDialog();

  const handleSearchingTimeout = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dataSearch, setDataSearch] = useState({
    keyword: null,
    page: 0,
    page_size: 100,
  });

  const [blogs, setBlogs] = useState([]);
  const [selectedBlogs, setSelectedBlogs] = useState([]);

  // const [formData, setFormData] = useState({
  //   image: null,
  // });

  const addBlog = (blog) => {
    if (blog) {
      setSelectedBlogs((prev) => {
        if (prev.length < MAX_BLOG_SUGGESTION) {
          return [...prev, blog];
        } else {
          enqueueSnackbar(
            `คุณได้เพิ่มบทความครบ ${MAX_BLOG_SUGGESTION} บทความแล้ว`,
            {
              variant: "error",
            },
          );
          return prev;
        }
      });
    }
  };

  const removeBlog = (id) => {
    setSelectedBlogs((prev) => {
      return prev.filter((item) => item._id !== id);
    });
  };

  const handleSubmit = async () => {
    try {
      await updateBlogSuggest({
        blog_suggest: selectedBlogs.map((item) => item._id),
      });

      enqueueSnackbar(`แก้ไขสำเร็จ`, {
        variant: "success",
      });
      onSuccess();
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSuggestionBlogs = async () => {
    try {
      const result = await getBlogSuggest();
      setSelectedBlogs(result);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchBlogs = async () => {
    try {
      const response = await getAllBlog({ ...dataSearch, page: 0 });
      const rows = response?.data?.rows ?? [];
      setBlogs(rows);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSearchMoods = () => {
    setIsLoading(true);
    clearTimeout(handleSearchingTimeout.current);
    handleSearchingTimeout.current = setTimeout(async () => {
      await fetchBlogs();
      setIsLoading(false);
    }, 500);
  };

  useEffect(() => {
    handleSearchMoods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearch.keyword]);

  useEffect(() => {
    fetchSuggestionBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: 1280,
            maxWidth: "100%",
          },
        },
        "& .MuiDialogContent-root": {
          pt: 1,
          pb: 4,
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack width="100%" alignItems="center">
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
            }}
          >
            แก้ไขรายการบทความแนะนำ
          </Typography>

          <Autocomplete
            // freeSolo
            blurOnSelect
            aria-required={true}
            getOptionSelected={(option, value) => option?._id === value?._id}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            renderOption={(props, option, { selected }) => {
              const { className } = props; // destruct only className, and create custom auto complete behavior
              return (
                <li
                  className={className}
                  key={`item-${option?._id}`}
                  style={{
                    cursor: "unset", // disable hover effect
                  }}
                >
                  <S.BlogOptionContainer>
                    <Box
                      sx={{
                        gridColumn: "1 / 7",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {!!option?.preview_image === false ? (
                        <ImageEmpty>
                          <InsertPhotoIcon
                            sx={{
                              color: "rgba(0, 0, 0, 0.54)",
                              cursor: "pointer",
                            }}
                          />
                        </ImageEmpty>
                      ) : (
                        <ImgDisplay
                          src={
                            option?.preview_image
                              ? option?.preview_image.replace(
                                  ".com",
                                  ".com/300x300",
                                )
                              : ""
                          }
                          alt={option?.preview_image}
                        />
                      )}
                      <Stack style={{ marginLeft: 16 }}>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: font.size.sm,
                          }}
                        >
                          {option?.title}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: font.size.sm,
                            color: DarkGrey,
                            marginTop: 0.5,
                          }}
                        >
                          {option?.sub_title}
                        </Typography>
                        <Stack
                          direction="row"
                          gap={1}
                          sx={{
                            marginTop: 0.5,
                          }}
                        >
                          {option?.hashtags?.slice(0, 3).map((hashtag) => (
                            <Chip
                              key={hashtag?.name}
                              label={hashtag?.name}
                              variant="outlined"
                              size="small"
                            />
                          ))}
                          {option?.hashtags?.length > 3 && (
                            <Chip
                              label={`+${option?.hashtags?.length - 3}`}
                              variant="outlined"
                              size="small"
                            />
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                    <Box
                      sx={{
                        gridColumn: "7 / 9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: font.size.sm,
                          color: DarkGrey,
                        }}
                      >
                        {moment(option?.createdAt).format("DD-MM-YYYY")}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        gridColumn: "9 / 11",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        variant="secondary"
                        size="large"
                        sx={{
                          minWidth: 160,
                          maxWidth: 350,
                        }}
                        startIcon={<AddIcon />}
                        onClick={() => {
                          addBlog(option);
                        }}
                        disabled={
                          selectedBlogs.findIndex(
                            (item) => item._id === option._id,
                          ) !== -1
                        }
                      >
                        เพิ่มบทความ
                      </Button>
                    </Box>
                  </S.BlogOptionContainer>
                </li>
              );
            }}
            options={blogs}
            value={null}
            onInputChange={(event, newInputValue) => {
              setDataSearch({
                ...dataSearch,
                keyword: newInputValue,
              });
            }}
            loading={isLoading}
            getOptionLabel={(option) => {
              return (
                option?.title +
                "-" +
                option?.sub_title +
                "-" +
                option?.hashtags?.map((style) => style.name).join(",")
              );
            }}
            filterOptions={createFilterOptions({
              matchFrom: "any",
              limit: 100,
            })}
            sx={{
              mt: 2,
              // mb: 3,
              width: "100%",
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  maxWidth: "100%",
                  width: "100%",
                }}
                placeholder="ค้นหาโดย ชื่อบทความ / แฮชแท็ก"
              />
            )}
          />
          <Typography
            variant="body1"
            sx={{
              alignSelf: "flex-start",
              my: 2,
              color: DarkGrey,
              fontSize: font.size.sm,
            }}
          >
            {`บทความ ${selectedBlogs.length}/${MAX_BLOG_SUGGESTION} รายการ`}
          </Typography>
          <Box
            style={{
              maxHeight: 700,
              overflowY: "auto",
            }}
          >
            <DraggableBoard
              items={selectedBlogs}
              onDragEnd={({ active, over }) => {
                if (active.id !== over?.id) {
                  setSelectedBlogs((prev) => {
                    const oldIndex = prev.findIndex(
                      (item) => item._id === active.id,
                    );
                    const newIndex = prev.findIndex(
                      (item) => item._id === over.id,
                    );

                    const newReArranged = arrayMove(prev, oldIndex, newIndex);
                    // moveBannerPosition(newReArranged);

                    return newReArranged;
                  });
                }
              }}
              Components={{
                DroppableParent: ({ items, children }) => {
                  return (
                    <S.BlogSuggestionContainer>
                      {children({ items })}
                    </S.BlogSuggestionContainer>
                  );
                },
                DraggableItem: (props) => {
                  const { items, item: blog, ...rest } = props; // Destructuring items to prevent this spreading to html element
                  return (
                    <S.BlogCardDraggable {...rest}>
                      <S.BlogCard
                        title={blog.title}
                        excerpt={blog.sub_title}
                        tags={blog?.hashtags.slice(0, 3)}
                        image={blog.preview_image}
                      />
                      <S.HoverContainer>
                        <S.ItemBackdrop />
                        <S.ItemActionMenu>
                          <IconButton
                            sx={{
                              color: White,
                              backgroundColor: hexToRgba(White, 0.4),
                            }}
                            onClick={() => {
                              removeBlog(blog._id);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </S.ItemActionMenu>
                      </S.HoverContainer>
                    </S.BlogCardDraggable>
                  );
                },
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
        }}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={onClose}
          sx={{
            minWidth: 160,
          }}
        >
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            openDialog({
              type: "confirm",
              title: `คุณต้องการบันทึกการแก้ไขใช่หรือไม่?`,
              onConfirm: async () => {
                await handleSubmit();
              },
              maxWidth: 342,
            });
          }}
          sx={{
            minWidth: 160,
          }}
          // autoFocus // enable auto focus to increase accessibility
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlogSuggestionAddDialog;
